/**
 * Selling Rate — what an hour of labour has to sell for (Coach sub-view, admin).
 *
 * The server (/api/sell-rate) reads the costs from QuickBooks and the hours from
 * the timesheets and hands them over as `inputs`. The arithmetic lives in
 * src/lib/sellRate.js and is run HERE, in the browser, every time a control
 * changes: that is what makes each box a live what-if. Save stores the plan;
 * Discard goes back to what was saved. Nothing else on this page writes anything.
 *
 *   SellRateView (named)   pure: draws from `data` (what the server last gave) and
 *                          `draft` (the settings being edited). Its only state is
 *                          which sections are open. No fetch, no router.
 *   SellRate (default)     the container: loads, saves, switches old notes off,
 *                          guards the page and hands everything to the view.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { usePermissions } from "../hooks/usePermissions";
import { apiFetch } from "../lib/apiFetch";
import {
  computeSellRate, normalizeSellRateSettings, OVERHEAD_LABELS,
  MAN_HOURS_PER_VAN_HOUR, HARD_MIN_VAN_RATE, HARD_MAX_VAN_RATE,
} from "../lib/sellRate";

const API = "/api/sell-rate";

const C = {
  bg: "#0d0f12", surface: "#161a20", surface2: "#1e2328", border: "#2a3140",
  accent: "#a5ce3a", accentDim: "rgba(165,206,58,.12)", text: "#e8eaf0",
  muted: "#8892a4", red: "#ef4444", redDim: "rgba(239,68,68,.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,.12)", yellow: "#eab308", yellowDim: "rgba(234,179,8,.10)",
};
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

// Chart colours: the app's blue and lime, one step darker so both sit in the
// readable band on the dark card and stay apart for colour-blind readers
// (checked with the palette validator against #161a20). The cost line is also
// dashed, so the two are never told apart by colour alone.
const CHART = { cost: "#3b82f6", earned: "#7ca228", grid: "rgba(255,255,255,0.06)", axis: "#8892a4" };
const chartTip = { background: "#161a20", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, padding: "8px 10px", fontSize: 12, color: "#f0f0f0" };

const JOB_TYPES = [
  "generator_install", "service_upgrade", "panel_swap", "hot_tub_feed", "ev_charger", "solar_battery",
  "lighting", "generator_service", "new_build", "commercial", "other",
];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const TRADE_WORDS = { jman: "Journeyman", apprentice: "Apprentice" };
const STATUS_WORDS = {
  new: "new: paid in full, counted at half the usual job hours until a full month is on the timesheets",
  short: "under 6 months, blended with the crew average",
  part_time: "part-time, counted as clocked",
};
const SOURCE_WORDS = {
  fixed: "a set rate",
  calculator: "following the calculator",
  code_default: "the built-in rate (nothing saved here yet)",
  family: "a job-type rate",
};
const PLANNED_WORDS = { field_hire: "Field hire", office_hire: "Office hire", cost: "Monthly cost" };
const PLANNED_HINTS = { field_hire: "e.g. Third-year apprentice", office_hire: "e.g. Office co-ordinator", cost: "e.g. Second van lease" };
const DASH = "—";

// ── Formatting. Anything from the server can be null: every one of these
//    answers with a dash rather than "NaN" or "$0". ───────────────────────────
const isNum = (v) => typeof v === "number" && Number.isFinite(v);
const clone = (o) => JSON.parse(JSON.stringify(o));
function money(v, digits = 0) {
  if (!isNum(v)) return DASH;
  const body = Math.abs(v).toLocaleString("en-CA", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const negative = v < 0 && Math.abs(v) >= 0.5 / 10 ** digits;
  return `${negative ? "-" : ""}$${body}`;
}
const hrs = (v, digits = 0) => (isNum(v) ? v.toLocaleString("en-CA", { minimumFractionDigits: digits, maximumFractionDigits: digits }) : DASH);
const pct = (v) => (isNum(v) ? `${v.toLocaleString("en-CA", { maximumFractionDigits: 1 })}%` : DASH);

// "2026-10-10" → "October 10, 2026". Built from the text, so no time zone can move the day.
function longDay(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ""));
  return m && MONTHS_LONG[Number(m[2]) - 1] ? `${MONTHS_LONG[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}` : "";
}
function longDayOf(iso) {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : `${MONTHS_LONG[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}
const monthShort = (ym) => { const n = MONTHS_LONG[Number(String(ym).slice(5, 7)) - 1]; return n ? n.slice(0, 3) : String(ym); };
const monthLong = (ym) => { const n = MONTHS_LONG[Number(String(ym).slice(5, 7)) - 1]; return n ? `${n} ${String(ym).slice(0, 4)}` : String(ym); };
const jobTypeLabel = (key) => {
  const s = String(key || "").replace(/_/g, " ");
  return (s.charAt(0).toUpperCase() + s.slice(1)).replace(/^Ev\b/, "EV");
};

// What a number box hands back: a number when the text IS a plain number, else
// the text exactly as typed ("" stays ""). Never NaN. The maths reads either.
const typed = (raw) => (raw === "" ? "" : String(Number(raw)) === raw ? Number(raw) : raw);

function tryCompute(inputs, settings, opts) {
  if (!inputs || !settings) return null;
  try {
    return computeSellRate(inputs, settings, opts);
  } catch (e) {
    return { ok: false, errors: [`The rate could not be worked out: ${String((e && e.message) || e)}`], warnings: [], assumptions: [] };
  }
}

// ── Small pieces ─────────────────────────────────────────────────────────────
function Btn({ children, onClick, disabled, kind = "ghost", size = "md", title }) {
  const styles = {
    ghost: { background: "transparent", color: C.text, border: `1px solid ${C.border}` },
    accent: { background: C.accent, color: "#0d0f12", border: `1px solid ${C.accent}`, fontWeight: 700 },
    danger: { background: C.redDim, color: C.red, border: "1px solid rgba(239,68,68,.4)" },
  }[kind];
  return (
    <button type="button" title={title} onClick={onClick} disabled={disabled} style={{
      ...styles, padding: size === "sm" ? "6px 10px" : "8px 14px", borderRadius: 8, fontFamily: "inherit",
      fontSize: size === "sm" ? 12.5 : 13.5, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap",
    }}>{children}</button>
  );
}

function Field({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 14, minWidth: 0, ...(style || {}) }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.45 }}>{hint}</div>}
    </div>
  );
}

// 16px so a phone does not zoom in when a box is tapped.
const inputStyle = {
  width: "100%", boxSizing: "border-box", background: C.bg, color: C.text, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: "7px 9px", fontSize: 16, fontFamily: "inherit", outline: "none",
};

function Card({ children, style }) {
  return <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 16px", marginBottom: 14, ...(style || {}) }}>{children}</div>;
}

function Pill({ children, color = C.muted, bg = "rgba(136,146,164,.14)" }) {
  return <span style={{ display: "inline-block", fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.3, padding: "2px 8px", borderRadius: 999, background: bg, color, whiteSpace: "nowrap" }}>{children}</span>;
}

function H2({ children, sub }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <h2 style={{ fontSize: 15.5, fontWeight: 800, margin: 0, color: C.text }}>{children}</h2>
      {sub && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3, lineHeight: 1.45 }}>{sub}</div>}
    </div>
  );
}

// A number box that keeps what was typed and turns red when the maths would
// ignore it (blank where a number is needed, or outside the allowed range).
function NumBox({ value, onChange, min, max, width = 92, prefix, suffix, placeholder, label, optional, disabled }) {
  const empty = value === "" || value === null || value === undefined;
  const n = Number(value);
  const bad = empty ? !optional : !(Number.isFinite(n) && (min === undefined || n >= min) && (max === undefined || n <= max));
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, maxWidth: "100%" }}>
      {prefix && <span style={{ color: C.muted, fontSize: 14 }}>{prefix}</span>}
      <input
        type="number" inputMode="decimal" step="any" min={min} max={max} disabled={disabled}
        value={empty ? "" : value} placeholder={placeholder} aria-label={label} aria-invalid={bad || undefined}
        title={bad && min !== undefined && max !== undefined ? `A number from ${min} to ${max}` : undefined}
        onChange={(e) => onChange(typed(e.target.value))}
        style={{ ...inputStyle, width, border: `1px solid ${bad ? C.red : C.border}` }}
      />
      {suffix && <span style={{ color: C.muted, fontSize: 14 }}>{suffix}</span>}
    </span>
  );
}

function Fold({ open, onToggle, label, children }) {
  return (
    <div>
      <button type="button" aria-expanded={!!open} onClick={onToggle}
        style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, padding: "6px 0", fontFamily: "inherit", textAlign: "left" }}>
        {open ? "▾" : "▸"} {label}
      </button>
      {open && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
}

// One of two choices. Only the heading is the button; what sits under it stays usable either way.
function Choice({ on, onPick, title, children }) {
  return (
    <div style={{ border: `1px solid ${on ? C.accent : C.border}`, background: on ? C.accentDim : "transparent", borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
      <button type="button" role="radio" aria-checked={!!on} onClick={onPick}
        style={{ display: "flex", alignItems: "center", gap: 10, background: "transparent", border: "none", padding: 0, cursor: "pointer", color: C.text, fontSize: 14.5, fontWeight: 700, fontFamily: "inherit", textAlign: "left" }}>
        <span aria-hidden style={{ width: 16, height: 16, borderRadius: 999, border: `2px solid ${on ? C.accent : C.muted}`, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto", boxSizing: "border-box" }}>
          {on && <span style={{ width: 8, height: 8, borderRadius: 999, background: C.accent }} />}
        </span>
        {title}
      </button>
      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  );
}

const th = { textAlign: "right", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.3, padding: "6px 10px", borderBottom: `1px solid ${C.border}`, verticalAlign: "bottom", lineHeight: 1.3 };
const thL = { ...th, textAlign: "left" };
const td = { textAlign: "right", fontSize: 13.5, padding: "8px 10px", borderBottom: `1px solid ${C.border}`, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: C.text };
const tdL = { ...td, textAlign: "left", whiteSpace: "normal" };
const para = { fontSize: 13.5, color: C.text, lineHeight: 1.55, margin: "8px 0 0" };
const small = { fontSize: 12.5, color: C.muted, lineHeight: 1.5, margin: "8px 0 0" };

// Every table sits in one of these, so a wide one scrolls sideways on a phone
// instead of pushing the page wider.
function TableWrap({ children, minWidth }) {
  return (
    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <table style={{ width: "100%", minWidth, borderCollapse: "collapse" }}>{children}</table>
    </div>
  );
}

function Tile({ label, value, unit, sub, accent }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${accent ? C.accent : C.border}`, borderRadius: 12, padding: "14px 16px", minWidth: 0 }}>
      <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4, color: C.text, fontVariantNumeric: "tabular-nums" }}>
        {value}{unit && <span style={{ fontSize: 12, fontWeight: 500, color: C.muted }}> {unit}</span>}
      </div>
      {sub && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, lineHeight: 1.45 }}>{sub}</div>}
    </div>
  );
}

// ── C. The four headline numbers ─────────────────────────────────────────────
function Tiles({ result, quoting, dirty }) {
  const van = result.rates.van;
  const target = (result.rates.ladder || []).find((r) => r.is_target) || null;
  // The built-in rate is also what the server falls back to when it cannot read
  // the saved setting; it says so in `detail`, and "nothing saved" would be wrong.
  const source = quoting.source === "fixed_fallback"
    ? `set rate${quoting.detail ? ` (${quoting.detail})` : ""}`
    : quoting.source === "code_default" && quoting.detail
      ? `the built-in rate (${quoting.detail})`
      : SOURCE_WORDS[quoting.source] || "";
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
        <Tile label="Break-even" value={money(van.break_even)} unit="a van-hour" sub="nothing left over at this rate" />
        <Tile accent label={`For ${pct(result.target_margin_pct)} profit`} value={money(van.target)} unit="a van-hour"
          sub={(
            <>
              {isNum(van.target_low) && isNum(van.target_high) && van.target_high - van.target_low >= 5 && (
                <span style={{ display: "block" }}>likely between {money(van.target_low)} and {money(van.target_high)}</span>
              )}
              {target && <span style={{ display: "block" }}>T&amp;M: {money(target.tm_jman)} journeyman, {money(target.tm_app)} apprentice</span>}
            </>
          )} />
        <Tile label="New quotes open at" value={money(quoting.rate)} unit="a van-hour"
          sub={(
            <>
              {source && <span style={{ display: "block" }}>{source}</span>}
              {isNum(van.recent) && isNum(van.margin_at_recent_pct) ? (
                <>
                  <span style={{ display: "block" }}>quotes are going out at about {money(van.recent)} on average: about {pct(van.margin_at_recent_pct)} profit at today's costs</span>
                  {isNum(van.margin_at_now_pct) && <span style={{ display: "block" }}>{pct(van.margin_at_now_pct)} if every hour sold at {money(van.now)}</span>}
                </>
              ) : (
                isNum(van.margin_at_now_pct) && <span style={{ display: "block" }}>about {pct(van.margin_at_now_pct)} profit at today's costs, if every hour sold at this rate</span>
              )}
              {dirty && <span style={{ display: "block" }}>this is the saved rate; your changes are not saved yet</span>}
            </>
          )} />
        <Tile label="Cost to cover each month" value={money(result.forward.monthly_cost)}
          sub={`${hrs(result.forward.job_hours)} job hours a month, about ${hrs(result.crew.vans_billable, 1)} vans of crew`} />
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>A van-hour is two people for one hour.</div>
    </div>
  );
}

// ── D. Profit target and which months to go by ───────────────────────────────
function Controls({ draft, ns, result, other, set }) {
  const sliderAt = Math.min(35, Math.max(0, Math.round(Number(draft.target_margin_pct)) || 0));
  const onRun = ns.basis === "run";
  let compare = null;
  if (result && result.ok && other && other.ok) {
    compare = onRun
      ? `On the last 12 months instead: ${money(other.rates.van.target)} a van-hour. Jobs earned ${money(other.rates.earned_per_job_hour)} per hour worked over the year against ${money(result.rates.earned_per_job_hour)} in the last 3 months.`
      : `On the last 3 months instead: ${money(other.rates.van.target)} a van-hour. Jobs earned ${money(other.rates.earned_per_job_hour)} per hour worked in those months against ${money(result.rates.earned_per_job_hour)} over the year.`;
  } else if (result && result.ok && other && other.errors && other.errors[0]) {
    compare = `The last ${onRun ? "12" : "3"} months cannot be worked out: ${other.errors[0]}`;
  }
  const seg = (on) => ({
    padding: "8px 14px", background: on ? C.accent : "transparent", color: on ? "#0d0f12" : C.muted, border: "none",
    fontSize: 13.5, fontWeight: on ? 700 : 600, cursor: "pointer", fontFamily: "inherit",
  });
  return (
    <Card>
      <div style={{ display: "flex", gap: "4px 28px", flexWrap: "wrap" }}>
        <Field label="Profit target" hint="Profit before income tax, as a share of sales." style={{ flex: "1 1 280px" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <NumBox value={draft.target_margin_pct} onChange={(v) => set({ target_margin_pct: v })} min={0} max={60} width={78} suffix="%" label="Profit target, percent" />
            <input type="range" min={0} max={35} step={1} value={sliderAt} aria-label="Profit target"
              onChange={(e) => set({ target_margin_pct: Number(e.target.value) })}
              style={{ flex: "1 1 120px", minWidth: 0, accentColor: C.accent }} />
          </div>
        </Field>
        <Field label="Based on" style={{ flex: "0 1 auto" }}>
          <div style={{ display: "inline-flex", border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
            <button type="button" aria-pressed={!onRun} onClick={() => set({ basis: "year" })} style={seg(!onRun)}>Last 12 months</button>
            <button type="button" aria-pressed={onRun} onClick={() => set({ basis: "run" })} style={seg(onRun)}>Last 3 months</button>
          </div>
        </Field>
      </div>
      {compare && <p style={{ ...small, margin: 0 }}>{compare}</p>}
    </Card>
  );
}

// ── E. How the rate is built ─────────────────────────────────────────────────
function BuildSection({ result, ns }) {
  const y = result.actual.year, r3 = result.actual.run, f = result.forward;
  const rates = result.rates, ref = result.reference;
  const profit = (a) => (isNum(a.net) ? `${money(a.net)} (${pct(a.net_margin_pct)})` : DASH);
  const rows = [
    ["Sales a month", money(y.revenue), money(r3.revenue), DASH],
    ["Materials, subs and permits at cost", money(y.passthrough), money(r3.passthrough), DASH],
    ["Field wages with payroll costs", money(y.wages_loaded), money(r3.wages_loaded), money(f.wages_loaded)],
    ["Overhead", money(y.overhead), money(r3.overhead), money(f.overhead)],
    ["Profit before tax", profit(y), profit(r3), DASH],
    ["Hours booked to jobs a month", hrs(y.job_hours), hrs(r3.job_hours), hrs(f.job_hours)],
    ["Wages per job hour", money(y.wage_per_job_hour, 2), money(r3.wage_per_job_hour, 2), money(f.wage_per_job_hour, 2)],
    ["Overhead per job hour", money(y.overhead_per_job_hour, 2), money(r3.overhead_per_job_hour, 2), money(f.overhead_per_job_hour, 2)],
    ["Cost per job hour", money(y.cost_per_job_hour, 2), money(r3.cost_per_job_hour, 2), money(f.cost_per_job_hour, 2), true],
    ["Earned per job hour", money(y.earned_per_job_hour, 2), money(r3.earned_per_job_hour, 2), DASH],
  ];
  const need = rates.needed_per_job_hour || {};
  const months = ns.basis === "run" ? "3" : "12";
  const earnedToday = isNum(rates.earned_per_job_hour_today) ? rates.earned_per_job_hour_today : rates.earned_per_job_hour;
  const mixMoved = isNum(result.crew.mix_factor) && Math.abs(result.crew.mix_factor - 1) >= 0.005;
  const sales = f.sales_needed || {};
  return (
    <Card>
      <H2>How the rate is built</H2>
      <TableWrap minWidth={560}>
        <thead>
          <tr>
            <th style={thL}>Monthly averages</th>
            <th style={th}>Last 12 months</th>
            <th style={th}>Last 3 months</th>
            <th style={th}>Today's crew and costs</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([name, a, b, c, bold]) => (
            <tr key={name}>
              <td style={{ ...tdL, fontWeight: bold ? 800 : 400 }}>{name}</td>
              <td style={{ ...td, fontWeight: bold ? 800 : 400 }}>{a}</td>
              <td style={{ ...td, fontWeight: bold ? 800 : 400 }}>{b}</td>
              <td style={{ ...td, fontWeight: bold ? 800 : 400 }}>{c}</td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <p style={{ ...para, marginTop: 14 }}>
        To cover costs, each hour worked on a job has to bring in {money(need.break_even, 2)} of labour sales; for {pct(result.target_margin_pct)} profit, {money(need.target, 2)}.
      </p>
      <p style={para}>
        Work in the last {months} months was quoted at {money(ref.van_rate)} a van-hour and brought in {money(rates.earned_per_job_hour, 2)} per hour worked,
        which is {pct(ref.yield_pct)} of a fully billed hour.
        {isNum(ref.yield_pct) && ref.yield_pct < 100 ? " The rest is hours that never become sales: quoted jobs that run over, travel, call-backs." : ""}
      </p>
      {mixMoved && (
        <p style={para}>
          Today's crew has {pct(result.crew.apprentice_share_pct)} apprentice hours against {pct((ns.basis === "run" ? r3 : y).apprentice_share_pct)} in those months,
          so an hour from today's crew is taken to bring in {money(earnedToday, 2)}.
        </p>
      )}
      <p style={para}>
        So the rate is {money(ref.van_rate)} × {money(need.target, 2)} / {money(earnedToday, 2)} = <b>{money(rates.van.target)} a van-hour</b>.
        {" "}If every hour worked were billed, {money(rates.van_fully_billed && rates.van_fully_billed.target)} would do it.
        {isNum(rates.van.target_low) && isNum(rates.van.target_high) && rates.van.target_high - rates.van.target_low >= 5
          ? ` Leaving any one month out of the sums moves it between ${money(rates.van.target_low)} and ${money(rates.van.target_high)}.` : ""}
      </p>
      {isNum(sales.target) && (
        <p style={para}>
          With today's crew and costs that is about {money(sales.target)} of sales a month for {pct(result.target_margin_pct)} profit, and {money(sales.break_even)} a month to break even.
        </p>
      )}
    </Card>
  );
}

// ── F. Rate for each profit level ────────────────────────────────────────────
function LadderSection({ result, quoting, tm }) {
  const ladder = result.rates.ladder || [];
  let todayAt = -1;
  if (isNum(quoting.rate)) {
    ladder.forEach((row, i) => {
      if (!isNum(row.van)) return;
      if (todayAt < 0 || Math.abs(row.van - quoting.rate) < Math.abs(ladder[todayAt].van - quoting.rate)) todayAt = i;
    });
  }
  return (
    <Card>
      <H2>Rate for each profit level</H2>
      <TableWrap minWidth={520}>
        <thead>
          <tr>
            <th style={thL}>Profit</th>
            <th style={th}>Van-hour</th>
            <th style={th}>T&amp;M journeyman</th>
            <th style={th}>T&amp;M apprentice</th>
            <th style={th}>If every hour were billed</th>
          </tr>
        </thead>
        <tbody>
          {ladder.map((row, i) => {
            const hot = !!row.is_target;
            const cell = { ...td, background: hot ? C.accentDim : "transparent", fontWeight: hot ? 800 : 400 };
            return (
              <tr key={row.margin_pct}>
                <td style={{ ...cell, textAlign: "left" }}>
                  {pct(row.margin_pct)}
                  {hot && <span style={{ marginLeft: 8 }}><Pill color={C.accent} bg="rgba(165,206,58,.18)">target</Pill></span>}
                  {i === todayAt && <span style={{ marginLeft: 8 }}><Pill>today</Pill></span>}
                </td>
                <td style={cell}>{money(row.van)}</td>
                <td style={cell}>{money(row.tm_jman)}</td>
                <td style={cell}>{money(row.tm_app)}</td>
                <td style={cell}>{money(row.van_fully_billed)}</td>
              </tr>
            );
          })}
        </tbody>
      </TableWrap>
      <p style={small}>
        Time-and-materials rates today: {money(tm.jman)} journeyman, {money(tm.app)} apprentice. They are not changed from this page.
      </p>
    </Card>
  );
}

// ── G. Crew on the tools ─────────────────────────────────────────────────────
function CrewSection({ result, draft, ns, onPlan }) {
  const crew = result.crew;
  const people = crew.people || [];
  const plans = draft.people && typeof draft.people === "object" ? draft.people : {};
  const vans = isNum(crew.job_hours) && crew.full_month_job_hours > 0 ? crew.job_hours / (MAN_HOURS_PER_VAN_HOUR * crew.full_month_job_hours) : null;
  const left = crew.left || [];
  const skipped = crew.skipped || [];
  return (
    <Card>
      <H2 sub="What each person books to jobs in a month, from the timesheets, holidays and slow weeks included.">Crew on the tools</H2>
      <TableWrap minWidth={720}>
        <thead>
          <tr>
            <th style={thL}>Name</th>
            <th style={thL}>Trade</th>
            <th style={th}>Job hours a month</th>
            <th style={th}>Last 3 months</th>
            <th style={th}>Share of a full month</th>
            <th style={th}>Plan</th>
            <th style={th}>Counted</th>
          </tr>
        </thead>
        <tbody>
          {people.map((p) => {
            const plan = plans[p.id] && plans[p.id].tools_pct !== undefined && plans[p.id].tools_pct !== null ? plans[p.id].tools_pct : "";
            const fromPlan = isNum(p.plan_share_pct);
            return (
              <tr key={p.id}>
                <td style={tdL}>
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  {STATUS_WORDS[p.status] && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{STATUS_WORDS[p.status]}</div>}
                </td>
                <td style={tdL}>{TRADE_WORDS[p.trade] || DASH}</td>
                <td style={td}>{hrs(p.auto_job_hours)}</td>
                <td style={td}>{hrs(p.recent_job_hours)}</td>
                <td style={td}>{pct(p.auto_share_pct)}</td>
                <td style={td}>
                  <NumBox value={plan} onChange={(v) => onPlan(p.id, v)} min={0} max={100} width={74} suffix="%" optional
                    placeholder={isNum(p.auto_share_pct) ? String(Math.round(p.auto_share_pct)) : ""} label={`Plan for ${p.name}, percent of a full month on the tools`} />
                </td>
                <td style={{ ...td, fontWeight: 700, color: fromPlan ? C.accent : C.text }}>{hrs(p.job_hours)}{fromPlan ? " (plan)" : ""}</td>
              </tr>
            );
          })}
        </tbody>
      </TableWrap>
      <p style={para}>{hrs(crew.job_hours)} job hours a month from today's crew, about {hrs(vans, 1)} vans.</p>
      {crew.measured && isNum(crew.measured.crews) && (
        <p style={small}>Over the last three months the timesheets show about {hrs(crew.measured.crews, 1)} crews out on a weekday.</p>
      )}
      <p style={small}>A full month is {hrs(ns.std_month_hours)} paid hours. Put 50 in the plan box to see a person at half their time on the tools.</p>
      {left.length > 0 && <p style={small}>Worked in this period but not on today's crew: {left.map((p) => p.name).join(", ")}.</p>}
      {skipped.length > 0 && (
        <div style={small}>
          Left out of the crew:
          <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{skipped.map((p) => <li key={p.id}>{p.name}: {p.reason}</li>)}</ul>
        </div>
      )}
    </Card>
  );
}

// ── H. Planned changes ───────────────────────────────────────────────────────
function PlannedSection({ result, noPlan, draft, ns, set }) {
  const planned = Array.isArray(draft.planned) ? draft.planned : [];
  const computed = (result && result.ok && result.planned && result.planned.rows) || [];
  const patchRow = (i, obj) => set({ planned: planned.map((p, k) => (k === i ? { ...p, ...obj } : p)) });
  const removeRow = (i) => set({ planned: planned.filter((_, k) => k !== i) });
  const addRow = (kind) => {
    const base = { id: `p${Date.now().toString(36)}`, kind, label: "", enabled: true, monthly_cost: 0 };
    // a planned hire starts from what an established person here is paid for and books to jobs
    const crew = (result && result.ok && result.crew) || {};
    const paid = isNum(crew.norm_paid_hours) && crew.norm_paid_hours > 0 ? Math.round(crew.norm_paid_hours) : ns.std_month_hours;
    const share = isNum(crew.norm_job_hours) && isNum(crew.norm_paid_hours) && crew.norm_paid_hours > 0 ? Math.min(100, Math.round((crew.norm_job_hours / crew.norm_paid_hours) * 100)) : 95;
    const row = kind === "field_hire" ? { ...base, trade: "jman", wage: 40, hours_per_month: paid, tools_pct: share } : base;
    set({ planned: [...planned, row] });
  };
  let effect = "Nothing is planned yet. Add a hire or a monthly cost to see what it does to the rate.";
  if (planned.length && planned.every((p) => p.enabled === false)) {
    effect = "Everything planned is switched off, so none of it is counted in the rate.";
  } else if (planned.length && result && result.ok && noPlan && noPlan.ok) {
    effect = `The plan adds ${money(result.forward.monthly_cost - noPlan.forward.monthly_cost)} a month of cost and ${hrs(result.planned.job_hours)} job hours. `
      + `That moves the rate for ${pct(result.target_margin_pct)} profit from ${money(noPlan.rates.van.target)} to ${money(result.rates.van.target)}.`;
  } else if (planned.length) {
    effect = "";
  }
  return (
    <Card>
      <H2 sub="Hires and costs that are coming but are not in the books yet. Switch one off to see the rate without it.">Planned changes</H2>
      {planned.map((p, i) => {
        const calc = computed.find((r) => r.id === String(p.id || "")) || null;
        const on = p.enabled !== false;
        return (
          <div key={p.id || i} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 12px 2px", marginBottom: 10, opacity: on ? 1 : 0.7 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: C.muted, cursor: "pointer" }}>
                <input type="checkbox" checked={on} onChange={(e) => patchRow(i, { enabled: e.target.checked })} style={{ width: 18, height: 18, accentColor: C.accent }} />
                {on ? "On" : "Off"}
              </label>
              <Pill>{PLANNED_WORDS[p.kind] || "Change"}</Pill>
              <input value={p.label || ""} onChange={(e) => patchRow(i, { label: e.target.value })} placeholder={PLANNED_HINTS[p.kind] || "Name"} aria-label="Name"
                style={{ ...inputStyle, flex: "1 1 180px", width: "auto", minWidth: 0, border: `1px solid ${String(p.label || "").trim() ? C.border : C.red}` }} />
              <Btn size="sm" kind="danger" onClick={() => removeRow(i)}>Remove</Btn>
            </div>
            <div style={{ display: "flex", gap: "0 16px", flexWrap: "wrap" }}>
              {p.kind === "field_hire" ? (
                <>
                  <Field label="Trade">
                    <select value={p.trade === "apprentice" ? "apprentice" : "jman"} onChange={(e) => patchRow(i, { trade: e.target.value })} style={{ ...inputStyle, width: "auto" }} aria-label="Trade">
                      <option value="jman">Journeyman</option>
                      <option value="apprentice">Apprentice</option>
                    </select>
                  </Field>
                  <Field label="Hourly wage">
                    <NumBox value={p.wage} onChange={(v) => patchRow(i, { wage: v })} min={1} max={300} width={84} prefix="$" label="Hourly wage" />
                  </Field>
                  <Field label="Hours a month">
                    <NumBox value={p.hours_per_month} onChange={(v) => patchRow(i, { hours_per_month: v })} min={1} max={320} width={84} label="Hours a month" />
                  </Field>
                  <Field label="Time on the tools">
                    <NumBox value={p.tools_pct} onChange={(v) => patchRow(i, { tools_pct: v })} min={0} max={100} width={74} suffix="%" label="Share of time on the tools, percent" />
                  </Field>
                  <Field label="Other monthly cost" hint="vehicle, tools">
                    <NumBox value={p.monthly_cost} onChange={(v) => patchRow(i, { monthly_cost: v })} min={0} max={100000} width={104} prefix="$" optional label="Other monthly cost" />
                  </Field>
                </>
              ) : (
                <Field label="Monthly cost">
                  <NumBox value={p.monthly_cost} onChange={(v) => patchRow(i, { monthly_cost: v })} min={1} max={250000} width={120} prefix="$" label="Monthly cost" />
                </Field>
              )}
            </div>
            {calc && (
              <div style={{ fontSize: 12.5, color: C.muted, margin: "-4px 0 12px", lineHeight: 1.45 }}>
                {!on ? "Switched off: not counted."
                  : p.kind === "field_hire" ? `Adds ${money(calc.monthly_total)} a month with payroll, holiday and statutory pay. Counted at ${hrs(calc.job_hours)} job hours: half of ${hrs(calc.job_hours_full)}, as for any new person until the timesheets show a full month.`
                    : `Adds ${money(calc.monthly_total)} a month.`}
              </div>
            )}
          </div>
        );
      })}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Btn onClick={() => addRow("field_hire")}>+ Field hire</Btn>
        <Btn onClick={() => addRow("office_hire")}>+ Office hire</Btn>
        <Btn onClick={() => addRow("cost")}>+ Monthly cost</Btn>
      </div>
      {effect && <p style={planned.length ? para : small}>{effect}</p>}
    </Card>
  );
}

// ── I. Overhead each month ───────────────────────────────────────────────────
function OverheadSection({ result, open, onToggle }) {
  const f = result.forward;
  const cats = f.overhead_by_cat || {};
  const keys = [...Object.keys(OVERHEAD_LABELS), ...Object.keys(cats).filter((k) => !(k in OVERHEAD_LABELS))];
  const rows = keys
    .map((k) => ({ key: k, name: OVERHEAD_LABELS[k] || jobTypeLabel(k), monthly: cats[k] }))
    .filter((r) => isNum(r.monthly) && Math.abs(r.monthly) >= 0.5);
  const plannedOverhead = result.planned ? result.planned.overhead : null;
  if (isNum(plannedOverhead) && Math.abs(plannedOverhead) >= 0.5) rows.push({ key: "_planned", name: "Planned changes", monthly: plannedOverhead });
  const perHour = (v) => (isNum(v) && f.job_hours > 0 ? money(v / f.job_hours, 2) : DASH);
  const lines = f.overhead_lines || [];
  const wear = result.wear || {};
  return (
    <Card>
      <H2 sub="What it costs to keep the doors open, at today's running rate.">Overhead each month</H2>
      <TableWrap minWidth={420}>
        <thead>
          <tr>
            <th style={thL}>What</th>
            <th style={th}>A month</th>
            <th style={th}>Per job hour</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td style={tdL}>{r.name}</td>
              <td style={td}>{money(r.monthly)}</td>
              <td style={td}>{perHour(r.monthly)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...tdL, fontWeight: 800, borderBottom: "none" }}>Total</td>
            <td style={{ ...td, fontWeight: 800, borderBottom: "none" }}>{money(f.overhead)}</td>
            <td style={{ ...td, fontWeight: 800, borderBottom: "none" }}>{perHour(f.overhead)}</td>
          </tr>
        </tbody>
      </TableWrap>
      {wear.source === "last_booked" && (
        <p style={small}>
          QuickBooks has booked no vehicle or equipment wear in the last 12 months, so {money(wear.monthly)} a month is used: what was last booked, spread evenly.
          You can set your own figure under Assumptions.
        </p>
      )}
      {wear.source === "none" && (
        <p style={small}>QuickBooks has booked no vehicle or equipment wear, so owned vehicles count as free here. Enter a monthly amount under Assumptions.</p>
      )}
      {lines.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Fold open={open} onToggle={onToggle} label={open ? "Hide the largest accounts" : "Show the largest accounts"}>
            <TableWrap minWidth={380}>
              <thead>
                <tr>
                  <th style={thL}>Account</th>
                  <th style={th}>A month</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={`${i}|${l.name}`}>
                    <td style={tdL}>
                      {l.name}
                      {l.steady === false && <span style={{ marginLeft: 8 }}><Pill>12-month average</Pill></span>}
                    </td>
                    <td style={td}>{money(l.monthly)}</td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            <p style={small}>
              An account that posted in each of the last three months is taken at its three-month average. Anything lumpier is taken at its 12-month average.
              {(f.overhead_ended || []).length > 0 ? ` Left out because they stopped posting: ${(f.overhead_ended || []).map((e) => e.name).join("; ")}.` : ""}
            </p>
          </Fold>
        </div>
      )}
    </Card>
  );
}

// ── J. Cost and earnings per hour worked ─────────────────────────────────────
function TrendSection({ result, open, onToggle }) {
  const round2 = (v) => (isNum(v) ? Math.round(v * 100) / 100 : null);
  const rows = (result.monthly || []).map((m) => ({ ym: m.ym, cost: round2(m.cost_3mo), earned: round2(m.earned_3mo) }));
  const drawn = rows.filter((r) => r.cost !== null || r.earned !== null);
  return (
    <Card>
      <H2>Cost and earnings per hour worked</H2>
      {drawn.length < 2 ? (
        <p style={small}>There are not enough months yet to draw this.</p>
      ) : (
        <div style={{ width: "100%", height: 240, overflow: "hidden" }}>
          <ResponsiveContainer>
            <LineChart data={rows} margin={{ left: 0, right: 12, top: 8, bottom: 4 }}>
              <CartesianGrid stroke={CHART.grid} vertical={false} />
              <XAxis dataKey="ym" tickFormatter={monthShort} tick={{ fill: CHART.axis, fontSize: 11 }} />
              <YAxis width={48} domain={["auto", "auto"]} tick={{ fill: CHART.axis, fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip contentStyle={chartTip} itemStyle={{ color: "#f0f0f0" }} labelStyle={{ color: C.muted, marginBottom: 2 }}
                labelFormatter={monthLong} formatter={(v, n) => [money(v, 2), n]} />
              <Legend wrapperStyle={{ fontSize: 12 }} formatter={(value) => <span style={{ color: C.muted }}>{value}</span>} />
              <Line dataKey="cost" name="Cost per job hour" stroke={CHART.cost} strokeWidth={2} strokeDasharray="6 4" legendType="plainline"
                dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} isAnimationActive={false} />
              <Line dataKey="earned" name="Earned per job hour" stroke={CHART.earned} strokeWidth={2} legendType="plainline"
                dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <p style={small}>Three-month rolling averages. Single months swing with when supplier bills land.</p>
      {drawn.length >= 2 && (
        <Fold open={open} onToggle={onToggle} label={open ? "Hide the numbers" : "Show the numbers"}>
          <TableWrap minWidth={340}>
            <thead>
              <tr>
                <th style={thL}>Three months to</th>
                <th style={th}>Cost per job hour</th>
                <th style={th}>Earned per job hour</th>
              </tr>
            </thead>
            <tbody>
              {drawn.map((r) => (
                <tr key={r.ym}>
                  <td style={tdL}>{monthLong(r.ym)}</td>
                  <td style={td}>{money(r.cost, 2)}</td>
                  <td style={td}>{money(r.earned, 2)}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Fold>
      )}
    </Card>
  );
}

// ── K. What new quotes open at ───────────────────────────────────────────────
function QuoteSection({ data, draft, ns, result, set, open, onToggle, onRetireNotes, busy, saving }) {
  const ok = !!(result && result.ok);
  const calcRate = ok && result.recommended && isNum(result.recommended.van_rate) ? result.recommended.van_rate : null;
  const last = data.last || {};
  const inForce = last.in_force && isNum(last.in_force.van_rate) ? last.in_force : null;
  const conflicts = Array.isArray(data.conflicts) ? data.conflicts : [];
  const family = draft.family_rates && typeof draft.family_rates === "object" ? draft.family_rates : {};
  const famRows = Object.entries(family);
  const unused = JOB_TYPES.filter((t) => !(t in family));
  const startRate = Number.isFinite(Number(draft.fixed_van_rate)) && draft.fixed_van_rate !== "" && draft.fixed_van_rate !== null ? Number(draft.fixed_van_rate) : ns.fixed_van_rate;
  const setFamily = (rows) => set({ family_rates: Object.fromEntries(rows) });
  const retire = () => {
    const n = conflicts.length;
    const ask = `Switch off ${n === 1 ? "this older quoting note" : `these ${n} older quoting notes`}?\n\nThe AI stops reading ${n === 1 ? "it" : "them"} when it writes a quote. ${n === 1 ? "The note is" : "The notes are"} kept, only switched off.`;
    if (window.confirm(ask)) onRetireNotes(conflicts.map((c) => c.id));
  };
  const limits = ns.max_step_per_month > 0
    ? `It moves by at most $${ns.max_step_per_month} every 30 days and stays between $${ns.floor} and $${ns.ceiling}.`
    : `It stays between $${ns.floor} and $${ns.ceiling}.`;
  return (
    <Card>
      <H2>What new quotes open at</H2>
      {!data.settings_exist && (
        <div style={{ background: C.yellowDim, border: "1px solid rgba(234,179,8,.3)", borderRadius: 10, padding: "10px 12px", fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>
          Nothing is saved here yet, so quoting works exactly as it did before. The boxes below start from what the older quoting notes say today. Save turns this setting on.
        </div>
      )}
      <div role="radiogroup" aria-label="What new quotes open at">
        <Choice on={ns.mode === "fixed"} onPick={() => set({ mode: "fixed" })} title="A set rate">
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <NumBox value={draft.fixed_van_rate} onChange={(v) => set({ fixed_van_rate: v })} min={HARD_MIN_VAN_RATE} max={HARD_MAX_VAN_RATE} width={96} prefix="$" suffix="a van-hour" label="The set rate, dollars a van-hour" />
            {calcRate !== null && <Btn size="sm" onClick={() => set({ fixed_van_rate: calcRate })}>Use the calculated rate ({money(calcRate)})</Btn>}
          </div>
          {ns.mode === "calculator" && <div style={{ ...small, marginTop: 6 }}>Still used as the fall-back if the calculated rate is missing, more than 45 days old or outside the limits.</div>}
        </Choice>
        <Choice on={ns.mode === "calculator"} onPick={() => set({ mode: "calculator" })} title="Follow the calculator">
          <div style={{ fontSize: 13, color: C.text, lineHeight: 1.55 }}>
            New quotes open at the rate calculated for your profit target on the last 12 months, rounded up to the nearest ${ns.rounding_step}. {limits}
            {calcRate !== null ? ` Today the calculation says ${money(calcRate)}.` : ""}
          </div>
          {inForce && (
            <div style={{ fontSize: 13, color: C.text, marginTop: 6 }}>
              In force now: <b>{money(inForce.van_rate)}</b>{inForce.since && longDay(inForce.since) ? `, since ${longDay(inForce.since)}` : ""}
              {isNum(inForce.target) && inForce.target !== inForce.van_rate ? `, moving toward ${money(inForce.target)}` : ""}
            </div>
          )}
        </Choice>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700 }}>Exceptions by job type</div>
        <div style={{ ...small, marginTop: 2, marginBottom: 10 }}>These job types open at their own rate, whichever choice is made above.</div>
        {famRows.map(([key, value]) => (
          <div key={key} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
            <select value={key} aria-label="Job type" style={{ ...inputStyle, width: "auto", flex: "0 1 220px", minWidth: 0 }}
              onChange={(e) => setFamily(famRows.map(([k, v]) => (k === key ? [e.target.value, v] : [k, v])))}>
              {[key, ...unused].map((t) => <option key={t} value={t}>{jobTypeLabel(t)}</option>)}
            </select>
            <NumBox value={value} onChange={(v) => setFamily(famRows.map(([k, old]) => (k === key ? [k, v] : [k, old])))} min={HARD_MIN_VAN_RATE} max={HARD_MAX_VAN_RATE} width={96} prefix="$" label={`Rate for ${jobTypeLabel(key)}, dollars a van-hour`} />
            <Btn size="sm" kind="danger" onClick={() => setFamily(famRows.filter(([k]) => k !== key))}>Remove</Btn>
          </div>
        ))}
        <Btn size="sm" disabled={!unused.length} onClick={() => setFamily([...famRows, [unused[0], startRate]])}>+ Add a job type</Btn>
      </div>

      <div style={{ marginTop: 14 }}>
        <Fold open={open} onToggle={onToggle} label="Limits">
          <div style={{ display: "flex", gap: "0 18px", flexWrap: "wrap" }}>
            <Field label="Lowest rate allowed">
              <NumBox value={draft.floor} onChange={(v) => set({ floor: v })} min={HARD_MIN_VAN_RATE} max={HARD_MAX_VAN_RATE} width={96} prefix="$" label="Lowest rate allowed" />
            </Field>
            <Field label="Highest rate allowed">
              <NumBox value={draft.ceiling} onChange={(v) => set({ ceiling: v })} min={HARD_MIN_VAN_RATE} max={HARD_MAX_VAN_RATE} width={96} prefix="$" label="Highest rate allowed" />
            </Field>
            <Field label="Round to the nearest">
              <select value={String(ns.rounding_step)} onChange={(e) => set({ rounding_step: Number(e.target.value) })} style={{ ...inputStyle, width: "auto" }} aria-label="Round to the nearest">
                <option value="1">$1</option>
                <option value="5">$5</option>
                <option value="10">$10</option>
              </select>
            </Field>
            <Field label="Most it may move in 30 days" hint="0 = no limit">
              <NumBox value={draft.max_step_per_month} onChange={(v) => set({ max_step_per_month: v })} min={0} max={200} width={96} prefix="$" label="Most the rate may move in 30 days" />
            </Field>
          </div>
        </Fold>
      </div>

      <p style={small}>This only sets the rate a new quote opens at. Quotes already written keep the rate they were written at.</p>
      {data.settings_updated_at && longDayOf(data.settings_updated_at) && (
        <p style={small}>Last changed by {data.settings_updated_by || "someone"} on {longDayOf(data.settings_updated_at)}.</p>
      )}

      {conflicts.length > 0 && (
        <div style={{ marginTop: 14, background: C.yellowDim, border: "1px solid rgba(234,179,8,.35)", borderRadius: 10, padding: "12px 14px", fontSize: 13, lineHeight: 1.5 }}>
          <b style={{ color: C.yellow }}>Older quoting notes still tell the AI a different rate:</b>
          <ul style={{ margin: "6px 0 10px", paddingLeft: 18, color: C.text }}>
            {conflicts.map((c) => <li key={c.id}>{c.text}</li>)}
          </ul>
          <Btn size="sm" disabled={!!busy || !!saving} onClick={retire}>{busy === "retire" ? "Switching off…" : "Switch these notes off"}</Btn>
        </div>
      )}
    </Card>
  );
}

// ── L. Assumptions ───────────────────────────────────────────────────────────
function AssumptionsSection({ result, draft, inputs, set, open, onToggle }) {
  const ok = !!(result && result.ok);
  const ratio = draft.apprentice_cost_ratio;
  const ratioShown = ratio === "" || ratio === null || ratio === undefined || !Number.isFinite(Number(ratio)) ? "" : Math.round(Number(ratio) * 10000) / 100;
  const sens = ok && result.rates ? result.rates.sensitivity : null;
  const list = (result && result.assumptions) || [];
  const hintMonth = inputs && inputs.hints ? MONTHS_LONG[Number(inputs.hints.year_end_month) - 1] : null;
  const ref = (ok && result.reference) || {};
  const earlier = draft.reference_rate_before_data;
  const showEarlier = (isNum(ref.months_without_quotes) && ref.months_without_quotes > 0) || !(earlier === null || earlier === undefined || earlier === "");
  return (
    <Card>
      <button type="button" aria-expanded={!!open} onClick={onToggle}
        style={{ display: "block", width: "100%", background: "transparent", border: "none", padding: 0, cursor: "pointer", color: C.text, fontFamily: "inherit", textAlign: "left" }}>
        <span style={{ fontSize: 15.5, fontWeight: 800 }}>{open ? "▾" : "▸"} Assumptions</span>
        <span style={{ display: "block", fontSize: 12.5, color: C.muted, marginTop: 3 }}>The few numbers the books cannot give, and how much each one moves the rate.</span>
      </button>
      {open && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", gap: "0 22px", flexWrap: "wrap" }}>
            <Field label="Markup on materials, subs and permits" hint="Your average, as a percent over cost.">
              <NumBox value={draft.passthrough_markup_pct} onChange={(v) => set({ passthrough_markup_pct: v })} min={0} max={100} width={84} suffix="%" label="Markup on materials, subs and permits, percent" />
            </Field>
            <Field label="Vehicle and equipment wear a month" hint="Empty = use what the books show.">
              <NumBox value={draft.wear_monthly} onChange={(v) => set({ wear_monthly: v === "" ? null : v })} min={0} max={100000} width={110} prefix="$" optional
                placeholder={ok && result.wear && isNum(result.wear.monthly) ? String(Math.round(result.wear.monthly)) : ""} label="Vehicle and equipment wear a month" />
            </Field>
            <Field label="A full month of paid hours" hint="For one person.">
              <NumBox value={draft.std_month_hours} onChange={(v) => set({ std_month_hours: v })} min={80} max={260} width={84} label="A full month of paid hours" />
            </Field>
            <Field label="Apprentice cost" hint="As a share of a journeyman's.">
              <NumBox value={ratioShown} min={20} max={100} width={84} suffix="%" label="Apprentice cost as a percent of a journeyman's"
                onChange={(v) => set({ apprentice_cost_ratio: v === "" || !Number.isFinite(Number(v)) ? "" : Number(v) / 100 })} />
            </Field>
            {showEarlier && (
              <Field label="Van rate before there is quote data"
                hint={isNum(ref.months_without_quotes) && ref.months_without_quotes > 0
                  ? `Quotes are on record for ${hrs(ref.months_with_quotes)} of these months. Enter what the other ${hrs(ref.months_without_quotes)} were quoted at, or leave it empty if not known.`
                  : "Empty = not known."}>
                <NumBox value={earlier} onChange={(v) => set({ reference_rate_before_data: v === "" ? null : v })} min={HARD_MIN_VAN_RATE} max={HARD_MAX_VAN_RATE} width={96} prefix="$" optional
                  label="Van rate quoted before there is quote data" />
              </Field>
            )}
            <Field label="Year-end month" hint={hintMonth ? `QuickBooks says ${hintMonth}. That month is kept out of the three-month view.` : "That month is kept out of the three-month view."}>
              <select value={draft.year_end_month === null || draft.year_end_month === undefined ? "" : String(draft.year_end_month)} aria-label="Year-end month"
                onChange={(e) => set({ year_end_month: e.target.value === "" ? null : Number(e.target.value) })} style={{ ...inputStyle, width: "auto" }}>
                <option value="">From QuickBooks</option>
                {MONTHS_LONG.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
              </select>
            </Field>
          </div>
          {list.length > 0 && (
            <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 13, color: C.text, lineHeight: 1.55 }}>
              {list.map((a, i) => <li key={i} style={{ marginBottom: 4 }}>{a}</li>)}
            </ul>
          )}
          {sens && (
            <div style={{ marginTop: 10 }}>
              {isNum(sens.per_1000_overhead) && <p style={small}>Each extra $1,000 a month of overhead adds {money(sens.per_1000_overhead, 2)} to the van rate.</p>}
              {isNum(sens.per_50_job_hours_lost) && <p style={small}>Every 50 job hours a month lost adds {money(sens.per_50_job_hours_lost, 2)}.</p>}
              {isNum(sens.markup_minus_5) && isNum(sens.markup_plus_5) && (
                <p style={small}>If the markup on materials is 5 points lower or higher, the rate is {money(sens.markup_minus_5)} or {money(sens.markup_plus_5)}.</p>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ── The page, drawn from props ───────────────────────────────────────────────
export function SellRateView({ data, draft, onDraft, dirty, saving, msg, errors, onSave, onDiscard, onRefresh, onRetireNotes, busy, onBack }) {
  const [open, setOpen] = useState({ limits: false, assumptions: false, accounts: false, months: false });

  // Every number on the page comes from these: the same maths the server runs,
  // on the server's inputs and whatever is in the boxes right now.
  const inputs = data ? data.inputs : null;
  const result = useMemo(() => tryCompute(inputs, draft), [inputs, draft]);
  const other = useMemo(() => tryCompute(inputs, draft, { basis: draft && draft.basis === "run" ? "year" : "run" }), [inputs, draft]);
  const noPlan = useMemo(
    () => (draft && Array.isArray(draft.planned) && draft.planned.length ? tryCompute(inputs, { ...draft, planned: [] }) : null),
    [inputs, draft],
  );
  const ns = useMemo(() => normalizeSellRateSettings(draft), [draft]);

  if (!data || !draft || !result) return null;

  const set = (obj) => { if (onDraft) onDraft({ ...draft, ...obj }); };
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const setPlan = (id, v) => {
    const people = { ...(draft.people && typeof draft.people === "object" ? draft.people : {}) };
    if (v === "") delete people[id]; else people[id] = { tools_pct: v };
    set({ people });
  };

  const ok = !!result.ok;
  const quoting = data.quoting || {};
  const pl = (inputs && inputs.pl) || {};
  const tm = (inputs && inputs.rates && inputs.rates.tm) || {};
  const warnings = result.warnings || [];
  const errorList = Array.isArray(errors) ? errors : [];
  const neverSaved = !data.settings_exist;
  const asOf = longDay(inputs && inputs.as_of);
  const costsFrom = pl.source === "snapshots" ? "Costs from the stored monthly copies of the books" : "Costs read live from QuickBooks";

  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, padding: "20px 16px 140px", fontFamily: FONT, boxSizing: "border-box" }}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>

        {/* A. Header */}
        <a href="/coach" onClick={(e) => { if (onBack) { e.preventDefault(); onBack(); } }}
          style={{ color: C.accent, fontSize: 13, textDecoration: "none", display: "inline-block", padding: "4px 0" }}>‹ Coach</a>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", margin: "4px 0 16px" }}>
          <div style={{ flex: "1 1 300px", minWidth: 0 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Selling Rate</h1>
            <p style={{ color: C.muted, fontSize: 13.5, margin: "6px 0 0", lineHeight: 1.5 }}>
              What an hour of labour has to sell for, worked out from QuickBooks costs and timesheet hours.
            </p>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>{asOf ? `${asOf} · ` : ""}{costsFrom}</div>
          </div>
          <Btn onClick={onRefresh} disabled={!!busy || !!saving}>{busy === "refresh" ? "Refreshing…" : "Refresh"}</Btn>
        </div>

        {!ok && (
          <Card style={{ borderColor: "rgba(239,68,68,.4)", background: C.redDim }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{(result.errors && result.errors[0]) || "The rate could not be worked out."}</div>
            {dirty && <div style={{ ...small, marginTop: 4 }}>If this started after a change on this page, Discard takes you back to what was saved.</div>}
          </Card>
        )}

        {/* B. Things to know */}
        {warnings.length > 0 && (
          <div style={{ background: C.yellowDim, border: "1px solid rgba(234,179,8,.3)", borderRadius: 12, padding: "12px 16px", marginBottom: 14 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: C.yellow, textTransform: "uppercase", letterSpacing: 0.4 }}>Things to know</div>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 13, color: C.text, lineHeight: 1.55 }}>
              {warnings.map((w, i) => <li key={i} style={{ marginBottom: 2 }}>{w}</li>)}
            </ul>
          </div>
        )}

        {ok && <Tiles result={result} quoting={quoting} dirty={dirty} />}
        <Controls draft={draft} ns={ns} result={result} other={other} set={set} />
        {ok && (
          <>
            <BuildSection result={result} ns={ns} />
            <LadderSection result={result} quoting={quoting} tm={tm} />
            <CrewSection result={result} draft={draft} ns={ns} onPlan={setPlan} />
          </>
        )}
        <PlannedSection result={result} noPlan={noPlan} draft={draft} ns={ns} set={set} />
        {ok && (
          <>
            <OverheadSection result={result} open={open.accounts} onToggle={() => toggle("accounts")} />
            <TrendSection result={result} open={open.months} onToggle={() => toggle("months")} />
          </>
        )}
        <QuoteSection data={data} draft={draft} ns={ns} result={result} set={set}
          open={open.limits} onToggle={() => toggle("limits")} onRetireNotes={onRetireNotes} busy={busy} saving={saving} />
        <AssumptionsSection result={result} draft={draft} inputs={inputs} set={set} open={open.assumptions} onToggle={() => toggle("assumptions")} />
      </div>

      {/* M. Save bar: only when something is waiting to be kept, or there is something to say */}
      {(dirty || neverSaved || msg || errorList.length > 0) && (
        <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, background: "rgba(22,26,32,.97)", borderTop: `1px solid ${C.border}`, padding: "10px 16px", zIndex: 50, fontFamily: FONT }}>
          <div style={{ maxWidth: 980, margin: "0 auto" }}>
            {errorList.length > 0 && (
              <div style={{ background: C.redDim, border: "1px solid rgba(239,68,68,.4)", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 8, maxHeight: "32vh", overflowY: "auto" }}>
                {/* by position: two rows with the same fault send the same sentence twice */}
                <ul style={{ margin: 0, paddingLeft: 18, color: C.text, lineHeight: 1.6 }}>{errorList.map((e, i) => <li key={i}>{e}</li>)}</ul>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ fontSize: 13, lineHeight: 1.45, minWidth: 0 }}>
                {dirty && <div style={{ color: C.yellow }}>Unsaved changes</div>}
                {!dirty && neverSaved && <div style={{ color: C.muted }}>Not switched on yet. Quoting is unchanged until this is saved.</div>}
                {msg && msg.text && <div role="status" style={{ color: msg.kind === "ok" ? C.green : C.red }}>{msg.text}</div>}
              </div>
              {(dirty || neverSaved) && (
                <div style={{ display: "flex", gap: 8 }}>
                  {dirty && <Btn onClick={onDiscard} disabled={!!saving}>Discard</Btn>}
                  {/* not while a refresh is in flight: its answer would land on top of the save's */}
                  <Btn kind="accent" onClick={onSave} disabled={!!saving || !!busy}>{saving ? "Saving…" : neverSaved ? "Save and turn on" : "Save"}</Btn>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── The container ────────────────────────────────────────────────────────────
export default function SellRate() {
  const navigate = useNavigate();
  const perms = usePermissions() || {};
  const allowed = !!(perms.isAdmin || perms.isOwner);

  const [data, setData] = useState(null);       // what the server last gave us
  const [draft, setDraft] = useState(null);     // the settings being edited
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState("");         // "" | "refresh" | "retire"
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);         // { kind: "ok" | "err", text }
  const [errors, setErrors] = useState([]);

  const call = useCallback(async (qs, opts) => {
    try {
      const r = await apiFetch(`${API}?${qs}`, opts);
      let j = {};
      try { j = await r.json(); } catch { j = {}; }
      if (!j || typeof j !== "object") j = {};
      if (!r.ok && !j.error) j.error = `The server answered ${r.status}`;
      if (!r.ok) j.ok = false;
      j.http_status = r.status;
      return j;
    } catch (e) {
      return { ok: false, error: String((e && e.message) || e) };
    }
  }, []);
  const post = useCallback((action, body) => call(`action=${action}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}),
  }), [call]);

  // A request that dies on the network, or a 5xx, is usually a function being
  // swapped mid-deploy: try again a couple of times before saying so. A refusal
  // (4xx) is an answer, not a blip.
  const fetchView = useCallback(async () => {
    let lastAnswer = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      const j = await call("action=get");
      if (j.ok && j.inputs && j.settings) return j;
      lastAnswer = j.ok ? { ok: false, error: "The server sent an answer this page could not read." } : j;
      if (j.http_status && j.http_status < 500) break;
      if (attempt < 2) await new Promise((r) => setTimeout(r, 700 * (attempt + 1)));
    }
    return lastAnswer;
  }, [call]);

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    const j = await fetchView();
    if (j && j.ok) { setData(j); setDraft(clone(j.settings)); setErrors([]); }
    else setLoadError((j && j.error) || "Could not load the selling rate.");
    setLoading(false);
  }, [fetchView]);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  // A confirmation clears itself; a problem stays until the next action.
  useEffect(() => {
    if (!msg || msg.kind !== "ok") return undefined;
    const t = setTimeout(() => setMsg(null), 6000);
    return () => clearTimeout(t);
  }, [msg]);

  const dirty = useMemo(() => !!(data && draft && JSON.stringify(draft) !== JSON.stringify(data.settings)), [data, draft]);

  const onDraft = useCallback((next) => { setDraft(next); }, []);

  const onSave = useCallback(async () => {
    if (!draft) return;
    const sent = draft;
    setSaving(true); setErrors([]); setMsg(null);
    const j = await post("save-settings", { settings: sent });
    setSaving(false);
    if (j.ok && j.inputs && j.settings) {
      // The save takes a few seconds. Anything typed while it was away is newer
      // than what was sent: keep it (it shows as unsaved) instead of wiping it.
      setData(j); setDraft((cur) => (cur === sent ? clone(j.settings) : cur));
      const rate = j.quoting && isNum(j.quoting.rate) ? ` New quotes open at ${money(j.quoting.rate)} a van-hour.` : "";
      setMsg({ kind: "ok", text: `Saved.${rate}` });
    } else if (j.ok && j.saved_only && j.settings) {
      // stored, and quoting already reads it; only the recalculation failed
      setData((d) => (d ? { ...d, settings: j.settings, settings_exist: true } : d));
      setDraft((cur) => (cur === sent ? clone(j.settings) : cur));
      setMsg({ kind: "err", text: `Saved, but the numbers could not be refreshed${j.error ? ` (${j.error})` : ""}. Press Refresh.` });
    } else {
      setErrors(Array.isArray(j.errors) ? j.errors : []);
      setMsg({ kind: "err", text: j.error || "Nothing was saved." });
    }
  }, [draft, post]);

  const onDiscard = useCallback(() => {
    if (data) setDraft(clone(data.settings));
    setErrors([]); setMsg(null);
  }, [data]);

  // Unsaved changes survive a refresh: only the server's side is replaced. The
  // check is made when the answer lands, not when the button was pressed, so a
  // box changed while the refresh was away is kept too.
  const onRefresh = useCallback(async () => {
    const before = data ? JSON.stringify(data.settings) : null;
    setBusy("refresh"); setMsg(null);
    const j = await fetchView();
    setBusy("");
    if (j && j.ok) {
      setData(j);
      setDraft((cur) => (!cur || JSON.stringify(cur) === before ? clone(j.settings) : cur));
      setMsg({ kind: "ok", text: "Refreshed." });
    } else {
      setMsg({ kind: "err", text: (j && j.error) || "Could not refresh." });
    }
  }, [fetchView, data]);

  const onRetireNotes = useCallback(async (ids) => {
    if (!Array.isArray(ids) || !ids.length) return;
    setBusy("retire"); setMsg(null);
    const j = await post("retire-notes", { ids });
    setBusy("");
    if (j.ok && j.inputs && j.settings) {
      setData(j);
      const n = Array.isArray(j.retired) ? j.retired.length : 0;
      setMsg(n
        ? { kind: "ok", text: `${n} older note${n === 1 ? "" : "s"} switched off.` }
        : { kind: "err", text: "No notes were switched off." });
    } else {
      setMsg({ kind: "err", text: j.error || "The notes could not be switched off." });
    }
  }, [post]);

  if (!allowed) {
    return <div style={{ padding: 40, color: C.muted, background: C.bg, minHeight: "100vh", fontFamily: FONT }}>Selling Rate is admin-only.</div>;
  }
  if (loading || !data || !draft) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", color: C.text, padding: "20px 16px 80px", fontFamily: FONT, boxSizing: "border-box" }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <button type="button" onClick={() => navigate("/coach")}
            style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", fontSize: 13, fontFamily: "inherit", padding: "4px 0" }}>‹ Coach</button>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: "4px 0 16px" }}>Selling Rate</h1>
          {loading ? (
            <div style={{ color: C.muted, fontSize: 13.5 }}>Reading the costs and the timesheets. This takes a few seconds…</div>
          ) : (
            <Card style={{ borderColor: "rgba(239,68,68,.4)" }}>
              <div style={{ fontSize: 13.5, color: C.red, lineHeight: 1.5, marginBottom: 10 }}>{loadError || "Could not load the selling rate."}</div>
              <Btn onClick={load}>Try again</Btn>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return (
    <SellRateView
      data={data} draft={draft} onDraft={onDraft} dirty={dirty} saving={saving} msg={msg} errors={errors}
      onSave={onSave} onDiscard={onDiscard} onRefresh={onRefresh} onRetireNotes={onRetireNotes} busy={busy}
      onBack={() => navigate("/coach")}
    />
  );
}
